<!-- eslint-disable vue/no-use-v-if-with-v-for -->
<template>
  <div>
    <div
      :style="indent"
      class="flex items-center space-x-2 cursor-pointer py-2 pr-9 hover:bg-gray-100"
      @click.self="toggleChildren"
    >
      <div
        class="h-5 w-5 pointer-events-none -mr-1 flex-shrink-0"
        v-if="props.children.length > 0"
      >
        <ChevronDownIcon
          v-if="expanded"
          class="text-gray-900"
          aria-hidden="true"
        />
        <ChevronRightIcon v-else class="text-gray-900" aria-hidden="true" />
      </div>
      <input
        v-if="!hideCheckbox"
        :indeterminate="hasSelectedChildren && !checked"
        :checked="checked"
        type="checkbox"
        class="h-4 w-4 ml-2 rounded-xs border-gray-300 text-brand-900 focus:ring-0 focus:outline-0 focus:ring-offset-0"
        @click.stop="handleClick"
      />
      <span
        class="flex pr-2 gap-2 justify-between pointer-events-none select-none whitespace-nowrap grow"
      >
        <span>{{ props.name }}</span>
        <span class="text-gray-400" v-if="props.showIdentifier">{{
          props.identifier
        }}</span>
      </span>
    </div>
    <tree-node
      v-if="expanded"
      v-for="child in children"
      :children="child.children"
      :id="child.id"
      :name="child.name"
      :identifier="child.identifier"
      :type="child.type"
      :tenant="props.tenant"
      :key="child.id"
      :depth="depth + 1"
      :activeIds="activeIds"
      :handle-checked="handleChecked"
      :is-searching="isSearching"
      :disable-ungrouped="props.disableUngrouped"
      :disable-groups="props.disableGroups"
      :show-identifier="props.showIdentifier"
    >
    </tree-node>
  </div>
</template>
<script setup>
import { computed, defineProps, ref, watch } from "vue";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/vue/20/solid";

const props = defineProps({
  id: String,
  name: String,
  type: String,
  tenant: String,
  children: Object,
  depth: Number,
  handleChecked: Function,
  isSearching: Boolean,
  activeIds: Array,
  disableUngrouped: Boolean,
  disableGroups: Boolean,
  disableTenants: Boolean,
  identifier: String,
  showIdentifier: Boolean,
});

const expanded = ref(!!props.isSearching);
const isSearching = ref(!!props.isSearching);

const hideCheckbox = computed(() => {
  if (props.disableTenants && props.type === "tenant") {
    return true;
  }

  if (
    props.disableGroups &&
    (props.type === "asset_group" || props.id.startsWith("ungrouped"))
  ) {
    return true;
  }

  return props.disableUngrouped && props.id.startsWith("ungrouped");
});

watch(
  () => props.isSearching,
  () => {
    isSearching.value = !!props.isSearching;
    expanded.value = !!props.isSearching;
  }
);

const indent = computed(() => {
  if (props.depth === 0) {
    return { paddingLeft: "1rem" };
  }

  return { paddingLeft: `${props.depth * 1 + 1}rem` };
});

function handleClick(e) {
  const checked = e.target.checked;
  props.handleChecked(
    { id: props.id, name: props.name, tenant: props.tenant },
    props.children,
    checked
  );
}

const checked = computed(() => {
  if (!props.activeIds?.length) {
    return false;
  }

  return props.activeIds.includes(props.id);
});

const checkForSelectedItemsRecursive = (items) => {
  if (!items?.length) {
    return false;
  }

  return items.some((child) => {
    let hasActiveChildren = false;

    if (Array.isArray(child.children) && child.children.length) {
      hasActiveChildren = checkForSelectedItemsRecursive(child.children);
    }

    return hasActiveChildren || props.activeIds.includes(child.id);
  });
};

const hasSelectedChildren = computed(() => {
  if (!props.children?.length) {
    return false;
  }

  return checkForSelectedItemsRecursive(props.children);
});

function toggleChildren() {
  expanded.value = !expanded.value;
}
</script>
